<script setup>
import { computed, ref } from 'vue';
import BaseBubble from 'next/message/bubbles/Base.vue';
import FormattedContent from './FormattedContent.vue';
import AttachmentChips from 'next/message/chips/AttachmentChips.vue';
import TranslationToggle from 'dashboard/components-next/message/TranslationToggle.vue';
import { MESSAGE_TYPES } from '../../constants';
import { useMessageContext } from '../../provider.js';
import { useTranslations } from 'dashboard/composables/useTranslations';

const { content, attachments, contentAttributes, messageType } =
  useMessageContext();

const { hasTranslations, translationContent } =
  useTranslations(contentAttributes);

const renderOriginal = ref(false);

const renderContent = computed(() => {
  if (renderOriginal.value) {
    return content.value;
  }

  if (hasTranslations.value) {
    return translationContent.value;
  }

  return content.value;
});

const isTemplate = computed(() => {
  return messageType.value === MESSAGE_TYPES.TEMPLATE;
});

const isEmpty = computed(() => {
  return !content.value && !attachments.value?.length;
});

const isDeleted = computed(() => {
  return (
    contentAttributes?.value?.deleted === true ||
    contentAttributes?.value?.is_deleted === true ||
    contentAttributes?.value?.isDeleted === true ||
    content.value === 'Mensagem apagada' ||
    content.value === 'Esta mensagem foi apagada'
  );
});

// Grupos WhatsApp (Cores e Nomes)
const WHATSAPP_PARTICIPANT_COLORS = [
  '#06b6d4',
  '#f59e0b',
  '#ec4899',
  '#8b5cf6',
  '#10b981',
  '#3b82f6',
  '#f97316',
  '#14b8a6',
  '#a855f7',
  '#e11d48',
];

const participantName = computed(() => {
  return (
    contentAttributes?.value?.sender_name ||
    contentAttributes?.value?.senderName ||
    ''
  );
});

const participantPhone = computed(() => {
  return (
    contentAttributes?.value?.sender_phone ||
    contentAttributes?.value?.senderPhone ||
    ''
  );
});

const isGroupMessage = computed(() => {
  const attrs = contentAttributes?.value || {};
  return (
    attrs.is_group === true ||
    attrs.isGroup === true ||
    Boolean(attrs.group_jid) ||
    Boolean(attrs.groupJid) ||
    Boolean(attrs.sender_name) ||
    Boolean(attrs.senderName)
  );
});

const isIncoming = computed(() => {
  return messageType.value === MESSAGE_TYPES.INCOMING;
});

const participantColor = computed(() => {
  const seed = participantName.value || participantPhone.value || '';
  if (!seed) return '#06b6d4';
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) {
    hash = (hash << 5) - hash + seed.charCodeAt(i);
    hash |= 0;
  }
  const index = Math.abs(hash) % WHATSAPP_PARTICIPANT_COLORS.length;
  return WHATSAPP_PARTICIPANT_COLORS[index];
});

const formattedPhone = computed(() => {
  const phone = participantPhone.value;
  if (!phone) return '';
  const clean = String(phone).replace(/\D/g, '');
  if (clean.length === 13 && clean.startsWith('55')) {
    return `+55 ${clean.slice(2, 4)} ${clean.slice(4, 9)}-${clean.slice(9)}`;
  }
  if (clean.length === 12 && clean.startsWith('55')) {
    return `+55 ${clean.slice(2, 4)} ${clean.slice(4, 8)}-${clean.slice(8)}`;
  }
  if (clean.length >= 10) {
    return `+${clean}`;
  }
  return phone;
});

const handleSeeOriginal = () => {
  renderOriginal.value = !renderOriginal.value;
};
</script>

<template>
  <BaseBubble class="px-4 py-3" data-bubble-name="text">
    <div
      v-if="isDeleted"
      class="flex items-center gap-1.5 text-slate-300/80 dark:text-slate-400 italic text-[13px] py-0.5 select-none min-w-[155px]"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="1.8"
        stroke-linecap="round"
        stroke-linejoin="round"
        class="size-4 opacity-75 shrink-0"
      >
        <circle cx="12" cy="12" r="10" />
        <line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
      </svg>
      <span>Mensagem apagada</span>
    </div>
    <div v-else class="gap-2 flex flex-col">
      <!-- Cabeçalho de Participante de Grupo (WhatsApp Style) -->
      <div
        v-if="isGroupMessage && isIncoming && (participantName || participantPhone)"
        class="flex items-center justify-between gap-3 text-xs -mb-0.5 font-medium select-none"
      >
        <span
          class="font-semibold truncate leading-tight tracking-tight"
          :style="{ color: participantColor }"
        >
          ~ {{ participantName || participantPhone }}
        </span>
        <span
          v-if="participantPhone && participantName"
          class="text-[10.5px] text-slate-400 font-mono tracking-tight shrink-0"
        >
          {{ formattedPhone }}
        </span>
      </div>

      <span v-if="isEmpty" class="text-n-slate-11">
        {{ $t('CONVERSATION.NO_CONTENT') }}
      </span>
      <FormattedContent v-if="renderContent" :content="renderContent" />
      <TranslationToggle
        v-if="hasTranslations"
        class="-mt-3"
        :showing-original="renderOriginal"
        @toggle="handleSeeOriginal"
      />
      <AttachmentChips :attachments="attachments" class="gap-2" />
      <template v-if="isTemplate">
        <div
          v-if="contentAttributes.submittedEmail"
          class="px-2 py-1 rounded-lg bg-n-alpha-3"
        >
          {{ contentAttributes.submittedEmail }}
        </div>
      </template>
    </div>
  </BaseBubble>
</template>

<style>
p:last-child {
  margin-bottom: 0;
}
</style>
